<template>
  <PreGameModal :progress="progress" :progressTotal="progressTotal">
    <template #content>
      <div class="content" v-if="error === Error.NO_HOST">
        <NoHost @back="$emit('back')" />
      </div>
      <PreGameModalWaitingError v-else-if="error !== null" @back="$emit('back')">
        <template #text v-if="error === Error.TIMEOUT">
          <b>{{ $t("game.preGameModalWaitingError.timeoutTitle") }}</b> {{ $t("game.preGameModalWaitingError.timeoutText") }}
        </template>
        <template #text v-else>
          <b>{{ $t("game.preGameModalWaitingError.genericTitle") }}</b> {{ $t("game.preGameModalWaitingError.genericText") }}
        </template>
      </PreGameModalWaitingError>
      <PreGameModalWaitingReady @go="go" v-else-if="cloudClient.pixelStreamingURL && !showIframe" />
      <PreGameModalWaitingProgress :loadingProgress="loadingProgress" v-else-if="!showIframe" />
    </template>
  </PreGameModal>
  <div v-if="iFrameEnabled && showIframe" :style="{'--crop': '-' + crop + 'px', '--videoCroppedHeight': videoCroppedHeight + 'px'}" class="fullscreen-iframe">
    <iframe :src="cloudClient.pixelStreamingURL" frameborder="0" allow="fullscreen" allowfullscreen></iframe>
    <button class="close-btn" @click="closeIframe">✕</button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";

import { useCloudClientStore } from "@/stores/cloud-client";

import PreGameModal from "./PreGameModal.vue";
import NoHost from "./PreGameModalChecks/2.3.1.NoHost.vue";
import PreGameModalWaitingProgress from "./waiting/partials/PreGameModalWaitingProgress.vue";
import PreGameModalWaitingReady from "./waiting/partials/PreGameModalWaitingReady.vue";
import PreGameModalWaitingError from "./waiting/partials/PreGameModalWaitingError.vue";

defineProps({
  progress: {
    type: Number,
    required: true,
  },
  progressTotal: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(["back"]);

enum Error {
  TIMEOUT,
  NO_HOST,
  OTHER,
}

const iFrameEnabled = import.meta.env.VITE_I_FRAME_ENABLED === "true";

const cloudClient = useCloudClientStore();

const time = ref(0);
const timeToWait = ref(180000);

const loadingProgress = computed(() => (time.value * 100) / timeToWait.value);
const error = ref<Error | null>(null);

const showIframe = ref(false);

let timeout: NodeJS.Timeout | null = null;
let launchInterval: NodeJS.Timeout | null = null;

onMounted(() => {
  cloudClient.startCloudSession();
  const now = Date.now();
  launchInterval = setInterval(() => {
    time.value = Date.now() - now;

    if (time.value >= timeToWait.value) {
      clearInterval(launchInterval);
      error.value = Error.OTHER;
    }
  }, 100);

  cloudClient.emitter.on("onCapacityError", () => {
    error.value = Error.NO_HOST;
  });

  cloudClient.emitter.on("onOtherError", () => {
    error.value = Error.OTHER;
  });
});


watch(cloudClient, (newCloudClient) => {
  if (newCloudClient.pixelStreamingURL) {
    if (timeout) clearTimeout(timeout);
    if (launchInterval) clearInterval(launchInterval);
    timeout = null;
    launchInterval = null;
    if (iFrameEnabled) {
      cloudClient.pixelStreamingURL = cloudClient.pixelStreamingURL.replace("http://", "https://");
      showIframe.value = true;
    }
    timeout = setTimeout(
      () => {
        error.value = Error.TIMEOUT;
      },
      1000 * 60 * 10
    ); // 10 minutes
  }
});

function go() {
  if (timeout) clearTimeout(timeout);
  if (launchInterval) clearInterval(launchInterval);
  timeout = null;
  launchInterval = null;

  if (iFrameEnabled) {
    cloudClient.pixelStreamingURL = cloudClient.pixelStreamingURL.replace("http://", "https://");
    showIframe.value = true;
  } else {
    const gameWindow = window.open(cloudClient.pixelStreamingURL, "_blank");

    if (gameWindow) {
      waitForGameWindowClosed(gameWindow, () => {
        window.location.reload();
      });
    }
  }
}

function waitForGameWindowClosed(window, cb) {
  function poll() {
    if (window.closed) cb();
    else requestAnimationFrame(poll);
  }
  poll();
}

function closeIframe() {
  showIframe.value = false;
  window.location.reload();
}

function goFullscreen() {
  const iframe = document.getElementById('myFrame');
  if (iframe.requestFullscreen) {
    iframe.requestFullscreen();
  } else if (iframe.webkitRequestFullscreen) { // Safari fallback
    iframe.webkitRequestFullscreen();
  }
}

function getIOSBrowserToolbarHeight() {
  // Full height of screen
  const fullHeight = window.screen.width;

  // Visible height of viewport
  const viewportHeight = window.innerHeight;

  // Toolbar height is the difference
  const toolbarHeight = fullHeight - viewportHeight;

  const diff =  toolbarHeight > 0 ? toolbarHeight : 0;
  return diff;
}

const videoCroppedHeight = ref(window.innerHeight * 1.24);
const crop = ref((window.innerHeight * 0.24) / 2);

function onResize() {
  videoCroppedHeight.value = window.innerHeight * 1.24;
  crop.value = (window.innerHeight * 0.24) / 2;
}

onMounted(() => {
  window.addEventListener('resize', onResize);
});

onUnmounted(() => {
  window.addEventListener('resize', onResize);
})

</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.fullscreen-iframe {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100svh;
  z-index: 5000;
  background: #000;
  display: flex;
  justify-content: center;
  align-items: center;

  overflow: hidden;

  @supports (-webkit-touch-callout: none) and (-webkit-overflow-scrolling: touch) {
      /* This is iOS Safari (and iOS WebView) */
      @media (orientation: landscape) {
        height: 100svh;
      }
  }

  iframe {
    position: absolute;
    width: 100vw;
    height: 100svh;
    border: none;
    top: 0;
    left: 0;

    @supports (-webkit-touch-callout: none) and (-webkit-overflow-scrolling: touch) {
      /* This is iOS Safari (and iOS WebView) */
      @media (orientation: landscape) {
        height: var(--videoCroppedHeight);
        top: var(--crop);
        left: 0;
      }
    }
  }

}
.fullscreen-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  background-color: #fff;
  border-radius: 50%;
  z-index: 9999;

}

.close-btn {
  position: absolute;
  top: 16px;
  left: 24px;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border: none;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;

  @media (orientation: portrait) {
    top: 24px;
    right: 16px;
    left: unset;
  }

  @media screen and (max-width: $small-screen) {
    width: 3rem;
    height: 3rem;
    font-size: 1.4rem;
  }
}
.content {
  display: flex;
  flex-direction: column;
  align-self: center;
  align-items: center;

  gap: 16px;
}
</style>
